//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { IScope } from 'angular';
import React from 'react';
import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import Indicator from '@components/atoms/indicator/Indicator';
import SidebarHeaderItem from '@components/atoms/sidebar-items/SidebarHeaderItem';
import SidebarLoadingPlaceholder from '@components/atoms/sidebar-items/SidebarLoadingPlaceholder';
import SidebarPageItemRouteless from '@components/atoms/sidebar-items/SidebarPageItemRouteless';
import Sidebar from '@components/organisms/sidebar/Sidebar';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { IStream } from '@streams-drawer/model/IStream';
import {
  nameToStreamType,
  StreamsDrawerStreamType,
  getRecommendedStreamName,
} from '@streams-drawer/streams-drawer-constants';
import { alphabetizeLocaleCompare } from '@utils/arrayUtils';

interface IOwnProps {
  streams: IStream[];
  hasRecommendedStreams: boolean;
  isRefreshingStreams: boolean;
  isRecommendationsClient: boolean;
  showRecommendedSummary: boolean;
  isModerationServices: boolean;
  onSummaryClick: () => void;
  noSelectedStream: boolean;
  recommendationStreams: IStream[];
  onStreamClick: ({ stream }: { stream: IStream }) => void;
  selectedStream: IStream;
  onOpenNewStreamModal: () => void;
  taggedByCuralatePendingCount: number;
  connectPendingCount: number;
  shouldNotAllowUgcStreams: boolean;
}

type Props = IOwnProps;

export const StreamsDrawerSidebarReact: React.FunctionComponent<Props> = ({
  streams,
  hasRecommendedStreams,
  isRecommendationsClient,
  isRefreshingStreams,
  showRecommendedSummary,
  isModerationServices,
  onSummaryClick,
  noSelectedStream,
  recommendationStreams,
  onStreamClick,
  selectedStream,
  onOpenNewStreamModal,
  taggedByCuralatePendingCount,
  connectPendingCount,
  shouldNotAllowUgcStreams,
}) => {
  const hasAlreadyCreatedStream =
    streams.filter(
      (stream) =>
        nameToStreamType(stream) !== StreamsDrawerStreamType.GALLERY_UPLOADS,
    ).length > 0;
  const isUgcOnlyClientAndStreamAlreadyCreated =
    shouldNotAllowUgcStreams && hasAlreadyCreatedStream;
  const shouldShowCreateStreamButton =
    !isModerationServices && !isUgcOnlyClientAndStreamAlreadyCreated;

  const pendingItemsCount = (stream: IStream) => {
    const isTaggedByCuralateStream =
      nameToStreamType(stream) === StreamsDrawerStreamType.TAGGED_BY_CURALTE;
    const isConnectStream =
      nameToStreamType(stream) === StreamsDrawerStreamType.CONNECT;
    if (isTaggedByCuralateStream) {
      return taggedByCuralatePendingCount;
    } else if (isConnectStream) {
      return connectPendingCount;
    } else {
      return null;
    }
  };

  const sidebarContent = (
    <>
      {isRecommendationsClient && (
        <>
          <SidebarHeaderItem text='Recommended Streams' />

          {showRecommendedSummary && (
            <SidebarPageItemRouteless
              text='All Recommendations'
              isActive={noSelectedStream}
              onClick={onSummaryClick}
            />
          )}

          {recommendationStreams.map((stream) => (
            <SidebarPageItemRouteless
              key={stream.id}
              isActive={selectedStream && selectedStream.id === stream.id}
              text={getRecommendedStreamName(stream)}
              onClick={() => onStreamClick({ stream })}
              linkBadge={
                <Indicator
                  showIf={(pendingItemsCount(stream) ?? 0) > 0}
                  title='You have unmoderated items'
                />
              }
            />
          ))}
        </>
      )}

      <SidebarHeaderItem
        text={`${hasRecommendedStreams ? 'Your' : ''} Media Streams`}
        headerAction={
          shouldShowCreateStreamButton ? (
            <ActionButton
              iconName='add'
              label='New Content Stream'
              onClick={onOpenNewStreamModal}
            />
          ) : (
            <></>
          )
        }
      />

      {alphabetizeLocaleCompare(streams, (stream) => stream.name).map(
        (stream) => (
          <SidebarPageItemRouteless
            key={stream.id}
            text={stream.name}
            isActive={selectedStream && selectedStream.id === stream.id}
            onClick={() => onStreamClick({ stream })}
          />
        ),
      )}
    </>
  );

  return (
    <Sidebar>
      {isRefreshingStreams ? <LoadingPlaceholder /> : sidebarContent}
    </Sidebar>
  );
};

const LoadingPlaceholder = styled(SidebarLoadingPlaceholder)`
  margin-top: 50px;
`;

angular.module('curalate').component('streamsDrawerSidebarReact', {
  bindings: {
    streams: '<',
    isRefreshingStreams: '<',
    isRecommendationsClient: '<',
    hasRecommendedStreams: '<',
    showRecommendedSummary: '<',
    isModerationServices: '<',
    onSummaryClick: '&',
    noSelectedStream: '<',
    recommendationStreams: '<',
    onStreamClick: '&',
    selectedStream: '<',
    onOpenNewStreamModal: '&',
    taggedByCuralatePendingCount: '<',
    connectPendingCount: '<',
    shouldNotAllowUgcStreams: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: IOwnProps } & IScope) => {
      return (
        <StreamsDrawerSidebarReact
          streams={scope.$ctrl.streams}
          isRefreshingStreams={scope.$ctrl.isRefreshingStreams}
          isRecommendationsClient={scope.$ctrl.isRecommendationsClient}
          hasRecommendedStreams={scope.$ctrl.hasRecommendedStreams}
          showRecommendedSummary={scope.$ctrl.showRecommendedSummary}
          isModerationServices={scope.$ctrl.isModerationServices}
          onSummaryClick={applyScope(scope, scope.$ctrl.onSummaryClick)}
          noSelectedStream={scope.$ctrl.noSelectedStream}
          recommendationStreams={scope.$ctrl.recommendationStreams}
          onStreamClick={applyScope(scope, scope.$ctrl.onStreamClick)}
          selectedStream={scope.$ctrl.selectedStream}
          onOpenNewStreamModal={applyScope(
            scope,
            scope.$ctrl.onOpenNewStreamModal,
          )}
          taggedByCuralatePendingCount={
            scope.$ctrl.taggedByCuralatePendingCount
          }
          connectPendingCount={scope.$ctrl.connectPendingCount}
          shouldNotAllowUgcStreams={scope.$ctrl.shouldNotAllowUgcStreams}
        />
      );
    },
  ),
});
